import { useState } from 'react';

const Blog = () => {
  // State for filtering and pagination
  const [selectedCategory, setSelectedCategory] = useState('All Posts');
  const [visibleCount, setVisibleCount] = useState(6);

  // Helper function to generate blog post URLs
  const getBlogPostUrl = (post: { id: number; slug?: string }) => {
    // All posts now have components, use the React component path
    const existingComponents = [
      'ai-agents-future',
      'implementing-ai-legacy-systems', 
      'roi-metrics-ai-projects',
      'ethical-ai-development',
      'custom-ai-vs-off-shelf',
      'ml-models-small-business',
      'data-privacy-ai-systems',
      'conversational-ai-customer-service',
      'predictive-analytics-ai' // Updated to match actual filename
    ];
    
    if (post.slug && existingComponents.includes(post.slug)) {
      return `/blog/${post.slug}`;
    }
    // Fallback for any new posts
    return `/blog/post/${post.id}`;
  };

  const blogPosts = [
    {
      id: 1,
      title: "The Future of AI Agents in Business Automation",
      excerpt: "Discover how AI agents are revolutionizing business processes and what to expect in the next five years.",
      author: "Sarah Chen",
      date: "March 15, 2024",
      readTime: "8 min read",
      category: "AI Technology",
      image: "/images/blog/ai-business-automation.jpg",
      featured: true,
      slug: "ai-agents-future"
    },
    {
      id: 2,
      title: "Implementing AI in Legacy Systems: A Practical Guide",
      excerpt: "Learn the best practices for integrating AI solutions with existing enterprise systems without disrupting operations.",
      author: "Michael Rodriguez",
      date: "March 10, 2024",
      readTime: "12 min read",
      category: "Implementation",
      image: "/images/blog/legacy-systems.jpg",
      featured: false,
      slug: "implementing-ai-legacy-systems"
    },
    {
      id: 3,
      title: "ROI Metrics for AI Projects: What Really Matters",
      excerpt: "Understanding how to measure the success of your AI initiatives beyond just cost savings.",
      author: "Jessica Liu",
      date: "March 5, 2024",
      readTime: "6 min read",
      category: "Business Strategy",
      image: "/images/blog/data-analytics.jpg",
      featured: false,
      slug: "roi-metrics-ai-projects"
    },
    {
      id: 4,
      title: "Ethical AI Development: Building Responsible Solutions",
      excerpt: "Exploring the importance of ethics in AI development and how to ensure your AI systems are fair and transparent.",
      author: "Dr. Amanda Foster",
      date: "February 28, 2024",
      readTime: "10 min read",
      category: "Ethics & AI",
      image: "/images/blog/ai-ethics.jpg",
      featured: false,
      slug: "ethical-ai-development"
    },
    {
      id: 5,
      title: "Custom AI vs. Off-the-Shelf Solutions: Making the Right Choice",
      excerpt: "A comprehensive comparison to help you decide between custom AI development and existing solutions.",
      author: "Robert Kim",
      date: "February 22, 2024",
      readTime: "9 min read",
      category: "Decision Making",
      image: "/images/blog/decision-making.jpg",
      featured: false,
      slug: "custom-ai-vs-off-shelf"
    },
    {
      id: 6,
      title: "Machine Learning Models for Small Businesses",
      excerpt: "How small and medium businesses can leverage AI without massive investments or technical teams.",
      author: "Maria Santos",
      date: "February 18, 2024",
      readTime: "7 min read",
      category: "Small Business",
      image: "/images/blog/small-business-ai.jpg",
      featured: false,
      slug: "ml-models-small-business"
    },
    {
      id: 7,
      title: "Data Privacy in AI Systems: Best Practices",
      excerpt: "Essential guidelines for maintaining data privacy and security when implementing AI solutions.",
      author: "David Park",
      date: "February 12, 2024",
      readTime: "11 min read",
      category: "Data Security",
      image: "/images/blog/ai-technology-1.jpg",
      featured: false,
      slug: "data-privacy-ai-systems"
    },
    {
      id: 8,
      title: "The Rise of Conversational AI in Customer Service",
      excerpt: "How AI-powered chatbots and virtual assistants are transforming customer interactions.",
      author: "Lisa Zhang",
      date: "February 8, 2024",
      readTime: "8 min read",
      category: "Customer Service",
      image: "/images/blog/conversational-ai.jpg",
      featured: false,
      slug: "conversational-ai-customer-service"
    },
    {
      id: 9,
      title: "AI-Powered Predictive Analytics: Real-World Applications",
      excerpt: "Exploring how businesses use predictive analytics to forecast trends and make better decisions.",
      author: "James Wilson",
      date: "February 1, 2024",
      readTime: "13 min read",
      category: "Predictive Analytics",
      image: "/images/blog/predictive-analytics.jpg",
      featured: false,
      slug: "predictive-analytics-ai"
    }
  ]

  const categories = [
    "All Posts",
    "AI Technology",
    "Implementation",
    "Business Strategy",
    "Ethics & AI",
    "Decision Making",
    "Small Business",
    "Data Security",
    "Customer Service",
    "Predictive Analytics"
  ]

  const featuredPost = blogPosts.find(post => post.featured)
  const regularPosts = blogPosts.filter(post => !post.featured)

  // Filter posts based on selected category
  const filteredPosts = selectedCategory === 'All Posts' 
    ? regularPosts 
    : regularPosts.filter(post => post.category === selectedCategory);

  // Get visible posts based on pagination
  const visiblePosts = filteredPosts.slice(0, visibleCount);
  const hasMorePosts = visibleCount < filteredPosts.length;

  // Handle category filter
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category);
    setVisibleCount(6); // Reset to show first 6 posts when category changes
  };

  // Handle load more posts
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 6);
  };

  return (
    <div className="blog-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 to-blue-50 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-8">
              AI Insights & Updates
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
              Learn & Discover{' '}
              <span className="block">
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  AI Innovation
                </span>
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-gray-600 leading-relaxed mb-8 max-w-3xl mx-auto">
              Stay up-to-date with the latest AI trends, implementation strategies, 
              and insights from our team of experts.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="#featured" 
                className="inline-flex items-center px-6 py-3 md:px-7 md:py-3.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="mr-3">Read Latest Posts</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </a>
              <a 
                href="/contact" 
                className="inline-flex items-center px-6 py-3 md:px-7 md:py-3.5 bg-transparent border-2 border-blue-600 text-blue-600 font-semibold rounded-lg hover:bg-blue-600 hover:text-white transition-all duration-300"
              >
                Get AI Consultation
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Post */}
      {featuredPost && (
        <section id="featured" className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
                Featured{' '}
                <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Article
                </span>
              </h2>
            </div>

            <div className="max-w-5xl mx-auto">
              <article className="bg-white rounded-2xl shadow-lg overflow-hidden">
                <div className="md:flex">
                  <div className="md:w-1/2">
                    <a 
                      href={getBlogPostUrl(featuredPost)}
                      className="flex h-80 bg-gray-200 items-center justify-center overflow-hidden rounded-l-2xl"
                    >
                      <img 
                        src={featuredPost.image} 
                        alt={featuredPost.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </a>
                  </div>
                  <div className="md:w-1/2 p-8">
                    <div className="flex items-center mb-4">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                        {featuredPost.category}
                      </span>
                      <span className="ml-4 text-sm text-gray-500">{featuredPost.readTime}</span>
                    </div>
                    
                    <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                      {featuredPost.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                      {featuredPost.excerpt}
                    </p>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                          {featuredPost.author.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div className="ml-3">
                          <p className="font-semibold text-gray-900">{featuredPost.author}</p>
                          <p className="text-sm text-gray-500">{featuredPost.date}</p>
                        </div>
                      </div>
                      
                      <a 
                        href={getBlogPostUrl(featuredPost)}
                        className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
                      >
                        Read More
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </section>
      )}

      {/* Blog Grid */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            {/* Categories Filter */}
            <div className="mb-12">
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category, index) => (
                  <button
                    key={index}
                    onClick={() => handleCategoryChange(category)}
                    className={`px-4 py-2 rounded-full font-semibold transition-colors duration-300 ${
                      selectedCategory === category 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-white text-gray-700 hover:bg-blue-600 hover:text-white'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Blog Posts Grid */}
            {filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {visiblePosts.map((post) => (
                  <article key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <a 
                      href={getBlogPostUrl(post)}
                      className="block h-48 bg-gray-200 overflow-hidden"
                    >
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </a>
                    
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                          {post.category}
                        </span>
                        <span className="text-sm text-gray-500">{post.readTime}</span>
                      </div>
                      
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">
                        {post.title}
                      </h3>
                      
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {post.excerpt}
                      </p>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                            {post.author.split(' ').map(n => n[0]).join('')}
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-semibold text-gray-900">{post.author}</p>
                            <p className="text-xs text-gray-500">{post.date}</p>
                          </div>
                        </div>
                        
                        <a 
                          href={getBlogPostUrl(post)}
                          className="text-blue-600 hover:text-blue-700 font-semibold text-sm"
                        >
                          Read More →
                        </a>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No articles found</h3>
                <p className="text-gray-600 mb-6">
                  No articles found in the "{selectedCategory}" category. Try selecting a different category.
                </p>
                <button 
                  onClick={() => handleCategoryChange('All Posts')}
                  className="px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  View All Posts
                </button>
              </div>
            )}

            {/* Load More Button */}
            {hasMorePosts && (
              <div className="text-center mt-12">
                <button 
                  onClick={handleLoadMore}
                  className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
                >
                  Load More Articles
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Newsletter Signup */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Stay Updated with{' '}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                AI Insights
              </span>
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Get the latest AI trends, implementation guides, and industry insights delivered to your inbox.
            </p>
            
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-6 py-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                required
              />
              <button
                type="submit"
                className="px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-300"
              >
                Subscribe
              </button>
            </form>
            
            <p className="text-sm text-gray-500 mt-4">
              No spam. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Implement AI in Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Let our experts help you turn these insights into actionable AI solutions.
            </p>
            <a 
              href="/contact" 
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              Get Expert Consultation
              <svg className="w-5 h-5 ml-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14m-7-7l7 7-7 7" />
              </svg>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}

export default Blog
